import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { GridContextProvider } from '@bingads-webui-grid/grid-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { Stack } from '@fluentui/react/lib/Stack';
import {
  Card,
  CardFooter,
  makeStyles,
  Text,
  Title3,
} from '@fluentui/react-components';
import { getTheme, SelectionMode } from '@fluentui/react';
import { DEFAULT_CAMPAIGN_SUBTYPE_FILTER } from '../constants/campaign';
import { getSelectedCampaignsGridColumns } from '../constants/selected-campaigns-grid-columns';
import {
  footerStyles,
  selectCampaignsStepLayoutTokens,
  selectCampaignsStyles
} from '../page-layout';
import { getUIBlockerStyle, noDataStyles } from '../styles';
import { validateCampaignCount } from '../validate';
import { SelectCampaignsGrid } from './select-campaigns-grid/select-campaigns-grid';
import { SelectCampaignsToolbar } from './select-campaigns-grid/select-campaigns-toolbar';
import { StudySummaryDrawer } from './summary/study-summary-drawer';
import { SelectCampaignsFooter } from './select-campaigns-grid/select-campaigns-footer';
import { SelectCampaignsGridDataSource } from './select-campaigns-grid/select-campaigns-grid-data-source';

const { palette } = getTheme();

const useStyles = makeStyles({
  footer: footerStyles,
  ...selectCampaignsStyles,
  noData: noDataStyles(palette),
});

export const SelectCampaignsStep = () => {
  useDefaultStyles(getUIBlockerStyle);
  const styles = useStyles();

  const i18n = useContext(I18nContext);
  const title = i18n.getString(_TL_('Choose one or more campaigns for this experiment'));
  const instructions = i18n.getString(_TL_('Select up to 10 campaigns. Please review the campaign eligibility requirements listed in the summary to ensure that your experiment runs successfully.'));

  const { state: wizardState, send } = useWizardService();
  const { wizardData } = wizardState.context;
  const {
    isValid,
    selectCampaignsError,
    showError,
  } = wizardData;

  const onChangeSelectedCampaigns = useCallback(({ count }) => {
    const results = validateCampaignCount({ count, i18n });
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      isValid: results.isValid,
      selectCampaignsError: results.error,
    });
  }, [i18n, send]);

  const initialGridContext = useMemo(() => ({
    editableFilter: DEFAULT_CAMPAIGN_SUBTYPE_FILTER,
    items: [],
    pagination: {
      pageNumber: 1,
      pageSize: 10,
    },
    selectedColumns: getSelectedCampaignsGridColumns(i18n),
    selectedItems: new Set(),
    selectionMode: SelectionMode.multiple,
    totalCount: 0,
  }), [i18n]);

  return (
    <div className={classNames(styles.root, 'select-campaigns-step')}>
      <GridContextProvider {...initialGridContext}>
        <SelectCampaignsGridDataSource />
        <div className={styles.content}>
          <Stack tokens={selectCampaignsStepLayoutTokens}>
            <Title3>{title}</Title3>
            <Text>{instructions}</Text>
            {(showError && !isValid) && (
              <MessageBar intent="error">
                <MessageBarBody>{selectCampaignsError}</MessageBarBody>
              </MessageBar>
            )}
            <SelectCampaignsToolbar />
            <Card>
              <div className="select-campaigns-grid">
                <SelectCampaignsGrid onCampaignsChange={onChangeSelectedCampaigns} />
              </div>
              <CardFooter className={styles.footer}>
                <SelectCampaignsFooter />
              </CardFooter>
            </Card>
          </Stack>
        </div>
        <StudySummaryDrawer onRemoveCampaign={onChangeSelectedCampaigns} />
      </GridContextProvider>
    </div>
  );
};
